import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatCurrency';
import { inZone } from '../utils/companyDate';
import { useCompanyTimezone } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;

// STORM DEPTH ENTRY (depth pricing design 3.3). The owner enters the measured
// storm depth for a depth-mode route, optionally a different depth per city,
// sees every completed stop's old and new price before anything is written
// (POST .../depth/preview writes nothing), and confirms with PUT .../depth,
// which reprices and relocks the route in one transaction on the server.
//
// Props:
//   show, onClose
//   route: { id, name, storm_id } of the route being entered
//   routes: the page's route list, for the same-storm checkbox (other
//     depth-mode routes with the same storm_id and no depth yet)
//   onSaved(result): called after a successful save, with the PUT response

const DEPTH_RE = /^\d{1,2}(\.\d)?$/;

// '' or a depth string, to a number or null. 0 to 99.9 at 0.1 precision.
const parseDepth = (v) => {
  const s = String(v == null ? '' : v).trim();
  if (s === '' || !DEPTH_RE.test(s)) return null;
  const n = Number(s);
  return n >= 0 && n <= 99.9 ? n : null;
};

const fmtIn = (n) => (n === null || n === undefined ? '' : `${Number(n).toFixed(1)} in`);
const money = (n) => (n === null || n === undefined ? '—' : formatCurrency(n));
const signed = (n) => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return formatCurrency(0);
  return `${n > 0 ? '+' : '-'}${formatCurrency(Math.abs(n))}`;
};

// Every refusal the depth endpoints give, in plain words.
export const depthErrorMessage = (err) => {
  const data = err?.response?.data || {};
  switch (data.code) {
    case 'DEPTH_PRICE_MISSING': {
      const list = (data.missing || [])
        .map((m) => (m.tier ? `${m.tier} at ${m.band || 'this depth'}` : 'a stop with no pricing tier'))
        .join(', ');
      return `Some prices are missing for this depth${list ? `: ${list}` : ''}. Set them in Settings, Snow pricing, then try again. Nothing was changed.`;
    }
    case 'DEPTH_CHANGES_INVOICED':
      return 'Some stops that would change were already invoiced. Tick the confirmation box to change them anyway. Nothing was changed.';
    case 'RETRY':
      return 'The route was busy, probably a stop being completed at the same moment. Try again.';
    case 'INVALID_DEPTH':
      return (data.errors && data.errors.length > 0) ? data.errors.join(' ') : 'That depth is not valid.';
    case 'NOT_DEPTH_ROUTE':
      return 'This route is not priced by snow depth.';
    default:
      return data.error || 'Something went wrong. Nothing was changed.';
  }
};

// A door payment on a stop awaiting depth (design 3.4) refused by
// PUT /api/route-stops/:stopId/payment, in plain words. null when the error is
// not one of these two.
export const doorPaymentErrorMessage = (err) => {
  const code = err?.response?.data?.code;
  if (code === 'AMOUNT_REQUIRED') return 'This stop is waiting for the storm depth, so enter the amount you collected.';
  if (code === 'NO_INVOICE_YET') return 'Square cannot be used until the storm depth is entered: no invoice exists yet. Invoice after the depth is entered, or record cash, check or another method with the amount collected.';
  return null;
};

// BILL THE DIFFERENCE on one stop whose invoice went out before a depth change
// (invoice_stale). Returns { ok, message } in plain words for every outcome:
// sent, no_increase, or a refusal. Used by Routes and Storm Mode.
export const billStopDifference = async (stopId) => {
  try {
    const res = await axios.post(`${API}/api/route-stops/${stopId}/bill-difference`);
    const d = res.data || {};
    if (d.sent) {
      return { ok: true, message: `Difference invoice for ${formatCurrency(d.amount)} sent${d.customer ? ` to ${d.customer}` : ''}.` };
    }
    if (d.reason === 'no_increase') {
      return { ok: true, message: `No increase over what was invoiced (${signed(d.difference)}), so nothing was sent. The stop is no longer marked out of date.` };
    }
    return { ok: true, message: 'Done.' };
  } catch (err) {
    const data = err?.response?.data || {};
    const byCode = {
      DIFFERENCE_ALREADY_INVOICED: 'A difference invoice was already sent for this stop. Void the invoices in Square and re-issue.',
      INVOICE_NOT_STALE: 'This stop has no out-of-date invoice.',
      NO_EMAIL: data.error || 'This customer has no email address.',
      NO_REPRICE: 'This stop has no depth change to bill.',
      INVOICE_SEND_FAILED: `The invoice could not be sent${data.error ? `: ${data.error}` : ''}. Nothing was recorded.`,
      TIER_REQUIRED: 'Bill the difference needs the Pro plan.',
    };
    return { ok: false, message: byCode[data.code] || data.error || 'The difference invoice could not be sent.' };
  }
};

function DepthEntryModal({ show, onClose, route, routes = [], onSaved }) {
  const timezone = useCompanyTimezone();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [depth, setDepth] = useState('');
  const [source, setSource] = useState('');
  const [cityDepths, setCityDepths] = useState({}); // cityKey -> string
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [confirmInvoiced, setConfirmInvoiced] = useState(false);
  const [alsoApply, setAlsoApply] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const previewSeq = useRef(0);

  const routeId = route?.id;

  // Other depth routes in the same storm still waiting for a depth.
  const sameStormRoutes = (route?.storm_id
    ? routes.filter((r) => r.id !== routeId && r.storm_id === route.storm_id
      && r.pricing_mode === 'depth' && (r.snow_depth_inches === null || r.snow_depth_inches === undefined)
      && !r.is_archived)
    : []);

  // Load the route's depth state each time the modal opens.
  useEffect(() => {
    if (!show || !routeId) return;
    let cancelled = false;
    setInfo(null); setLoadError(''); setPreview(null); setPreviewError('');
    setConfirmInvoiced(false); setAlsoApply(false); setSaveError(''); setResult(null);
    setHistory(null); setShowHistory(false);
    axios.get(`${API}/api/routes/${routeId}/depth`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        setInfo(d);
        setDepth(d.depthInches === null || d.depthInches === undefined ? '' : Number(d.depthInches).toFixed(1));
        setSource(d.depthSource || d.sourceDefault || '');
        const byKey = {};
        (d.overrides || []).forEach((o) => {
          const c = (d.cities || []).find((x) => x.city === o.city
            || String(x.cityKey) === String(o.city).trim().toLowerCase());
          if (c) byKey[c.cityKey] = Number(o.depthInches).toFixed(1);
        });
        setCityDepths(byKey);
      })
      .catch((err) => { if (!cancelled) setLoadError(depthErrorMessage(err)); });
    return () => { cancelled = true; };
  }, [show, routeId]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, saving, onClose]);

  const depthValue = parseDepth(depth);
  const cityErrors = Object.entries(cityDepths)
    .filter(([, v]) => String(v).trim() !== '' && parseDepth(v) === null)
    .map(([k]) => k);

  const buildBody = useCallback(() => {
    const overrides = (info?.cities || [])
      .filter((c) => String(cityDepths[c.cityKey] || '').trim() !== '')
      .map((c) => ({ city: c.city, depthInches: parseDepth(cityDepths[c.cityKey]) }))
      .filter((o) => o.depthInches !== null);
    return { depthInches: depthValue, depthSource: source.trim() || null, overrides };
  }, [info, cityDepths, depthValue, source]);

  // Debounced preview. Writes nothing on the server.
  useEffect(() => {
    if (!show || !info || result) return undefined;
    if (depthValue === null || cityErrors.length > 0) { setPreview(null); return undefined; }
    const seq = ++previewSeq.current;
    const t = setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await axios.post(`${API}/api/routes/${routeId}/depth/preview`, buildBody());
        if (seq !== previewSeq.current) return;
        setPreview(res.data);
        setPreviewError('');
      } catch (err) {
        if (seq !== previewSeq.current) return;
        setPreview(null);
        setPreviewError(depthErrorMessage(err));
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // cityErrors is derived from cityDepths, which buildBody already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, info, routeId, buildBody, result]);

  const invoicedChanging = preview?.invoiced || [];
  const missing = preview?.missingPrices || [];
  const canConfirm = !saving && !previewing && preview && depthValue !== null && cityErrors.length === 0
    && missing.length === 0 && (invoicedChanging.length === 0 || confirmInvoiced);

  const save = async () => {
    if (!canConfirm) return;
    setSaving(true); setSaveError('');
    const body = buildBody();
    try {
      const res = await axios.put(`${API}/api/routes/${routeId}/depth`,
        { ...body, confirmInvoiced: invoicedChanging.length > 0 ? confirmInvoiced : false });
      const out = { main: res.data, others: [] };
      // Same storm: one PUT per route, so each keeps its own transaction and
      // audit row. The per-city boxes belong to this route only.
      if (alsoApply && sameStormRoutes.length > 0) {
        for (const r of sameStormRoutes) {
          try {
            const o = await axios.put(`${API}/api/routes/${r.id}/depth`,
              { depthInches: body.depthInches, depthSource: body.depthSource, overrides: [] });
            out.others.push({ route: r, ok: true, data: o.data });
          } catch (err) {
            out.others.push({ route: r, ok: false, error: depthErrorMessage(err) });
          }
        }
      }
      setResult(out);
      onSaved?.(out);
    } catch (err) {
      setSaveError(depthErrorMessage(err));
      if (err?.response?.data?.code === 'DEPTH_CHANGES_INVOICED') setConfirmInvoiced(false);
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setShowHistory(true);
    if (history) return;
    try {
      const res = await axios.get(`${API}/api/routes/${routeId}/depth/changes`);
      setHistory(res.data.changes || []);
    } catch (err) {
      setHistory([]);
    }
  };

  if (!show || !route) return null;

  const autoSent = result?.main?.autoInvoice?.sent?.length || 0;
  const autoFailed = result?.main?.autoInvoice?.failed?.length || 0;

  return (
    <div className="depth-modal-overlay" onClick={() => { if (!saving) onClose?.(); }}>
      <div className="depth-modal" role="dialog" aria-modal="true" aria-labelledby="depth-modal-title"
        onClick={(e) => e.stopPropagation()}>
        <div className="depth-modal-header">
          <h2 id="depth-modal-title">Storm depth · {route.name}</h2>
          <button className="depth-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>

        <div className="depth-modal-body">
          {loadError && <div className="depth-alert depth-alert-error">{loadError}</div>}
          {!info && !loadError && <p className="depth-muted">Loading…</p>}

          {info && showHistory && (
            <div className="depth-history">
              <button className="btn btn-sm btn-outline" onClick={() => setShowHistory(false)}>Back</button>
              <h3>Depth history</h3>
              {history === null ? <p className="depth-muted">Loading…</p>
                : history.length === 0 ? <p className="depth-muted">No depth has been entered on this route yet.</p>
                : history.map((ch) => (
                  <div key={ch.id} className="depth-history-item">
                    <div className="depth-history-head">
                      <strong>{ch.oldDepthInches === null ? 'Entered' : `${fmtIn(ch.oldDepthInches)} to`} {fmtIn(ch.newDepthInches)}</strong>
                      <span className="depth-muted">
                        {new Date(ch.changedAt).toLocaleString(undefined, inZone(timezone))}{ch.changedBy?.name ? ` · ${ch.changedBy.name}` : ''}
                      </span>
                    </div>
                    <div className="depth-muted">
                      {ch.stopsRepriced} stop{ch.stopsRepriced === 1 ? '' : 's'} repriced · {money(ch.totalBefore)} to {money(ch.totalAfter)}
                      {Array.isArray(ch.newOverrides) && ch.newOverrides.length > 0
                        && ` · ${ch.newOverrides.map((o) => `${o.city} ${fmtIn(o.depthInches)}`).join(', ')}`}
                      {ch.depthSource ? ` · ${ch.depthSource}` : ''}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {info && !showHistory && result && (
            <div className="depth-result">
              <div className="depth-alert depth-alert-success">
                Depth saved: {fmtIn(result.main.depthInches)}. {result.main.stopsRepriced} stop
                {result.main.stopsRepriced === 1 ? '' : 's'} repriced, route total {money(result.main.totalAfter)}.
              </div>
              {autoSent > 0 && (
                <div className="depth-alert depth-alert-info">
                  {autoSent} Square invoice{autoSent === 1 ? '' : 's'} sent automatically now that the depth is known.
                </div>
              )}
              {autoFailed > 0 && (
                <div className="depth-alert depth-alert-error">
                  {autoFailed} automatic invoice{autoFailed === 1 ? '' : 's'} could not be sent. Use Bill All on the route.
                </div>
              )}
              {(result.main.trueUp || []).length > 0 && (
                <TrueUpBox rows={result.main.trueUp} />
              )}
              {result.others.map((o) => (
                <div key={o.route.id} className={`depth-alert ${o.ok ? 'depth-alert-success' : 'depth-alert-error'}`}>
                  {o.route.name}: {o.ok ? `${fmtIn(o.data.depthInches)} saved, ${o.data.stopsRepriced} stop${o.data.stopsRepriced === 1 ? '' : 's'} repriced.` : o.error}
                </div>
              ))}
            </div>
          )}

          {info && !showHistory && !result && (
            <>
              <div className="depth-row">
                <label className="depth-field">
                  <span>Storm depth (inches)</span>
                  <input type="number" inputMode="decimal" step="0.1" min="0" max="99.9"
                    className="form-input" value={depth} autoFocus
                    onChange={(e) => setDepth(e.target.value)} placeholder="e.g. 6.5" />
                </label>
                <label className="depth-field depth-field-wide">
                  <span>Source</span>
                  <input type="text" className="form-input" maxLength={120} value={source}
                    onChange={(e) => setSource(e.target.value)} placeholder="e.g. NWS storm total" />
                </label>
              </div>
              {depth !== '' && depthValue === null && (
                <p className="depth-field-error">Enter a depth from 0 to 99.9, at most one decimal place.</p>
              )}
              {info.pendingCount > 0 && (
                <p className="depth-muted">{info.pendingCount} completed stop{info.pendingCount === 1 ? ' is' : 's are'} waiting for this depth.</p>
              )}

              {(info.cities || []).length > 0 && (
                <div className="depth-section">
                  <h3>Per city <span className="depth-muted">(blank uses the route depth)</span></h3>
                  <table className="depth-table">
                    <thead><tr><th>City</th><th>Stops</th><th>Depth (in)</th></tr></thead>
                    <tbody>
                      {info.cities.map((c) => (
                        <tr key={c.cityKey}>
                          <td>{c.city || '(no city)'}</td>
                          <td>{c.stopCount}{c.completedCount > 0 ? ` (${c.completedCount} done)` : ''}</td>
                          <td>
                            <input type="number" inputMode="decimal" step="0.1" min="0" max="99.9"
                              className={`form-input depth-city-input ${cityErrors.includes(c.cityKey) ? 'invalid' : ''}`}
                              aria-label={`Depth for ${c.city}`}
                              value={cityDepths[c.cityKey] || ''} placeholder={depthValue === null ? '' : depthValue.toFixed(1)}
                              disabled={!c.city}
                              onChange={(e) => setCityDepths((prev) => ({ ...prev, [c.cityKey]: e.target.value }))} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="depth-section">
                <h3>Preview {previewing && <span className="depth-muted">updating…</span>}</h3>
                {previewError && <div className="depth-alert depth-alert-error">{previewError}</div>}
                {!preview && !previewError && <p className="depth-muted">Enter a depth to see each stop's new price.</p>}
                {preview && (
                  <>
                    {missing.length > 0 && (
                      <div className="depth-alert depth-alert-warning">
                        <strong>Prices missing.</strong> Nothing can be saved until these are set in Settings, Snow pricing:{' '}
                        {missing.map((m) => (m.tier ? `${m.tier} at ${m.band || 'this depth'}` : 'a stop with no pricing tier')).join(', ')}.
                      </div>
                    )}
                    {preview.stops.length === 0 ? (
                      <p className="depth-muted">No completed stops yet. The depth will price stops as they are completed.</p>
                    ) : (
                      <div className="depth-table-wrap">
                        <table className="depth-table depth-preview-table">
                          <thead>
                            <tr><th>Stop</th><th>City</th><th>Depth</th><th>Band</th><th>Old</th><th>New</th><th>Change</th></tr>
                          </thead>
                          <tbody>
                            {preview.stops.map((s) => (
                              <tr key={s.stopId} className={s.changed ? 'changed' : ''}>
                                <td>
                                  {s.customer}
                                  <span className="depth-badges">
                                    {s.flags.paid && <span className="depth-badge paid">Paid</span>}
                                    {s.flags.invoiced && <span className="depth-badge invoiced">Invoiced</span>}
                                    {s.flags.comp && <span className="depth-badge comp">Comp</span>}
                                  </span>
                                </td>
                                <td>{s.city || ''}</td>
                                <td>{s.depth === null ? '' : `${Number(s.depth).toFixed(1)}`}</td>
                                <td>{s.newBand || (s.flags.lawn ? 'Lawn' : '')}</td>
                                <td>{s.oldSource === 'pending_depth' ? 'Awaiting' : money(s.oldTotal)}</td>
                                <td>{money(s.newTotal)}</td>
                                <td className={s.delta > 0 ? 'up' : s.delta < 0 ? 'down' : ''}>{signed(s.delta)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan={4}>{preview.stopsRepriced} stop{preview.stopsRepriced === 1 ? '' : 's'} change</td>
                              <td>{money(preview.totalBefore)}</td>
                              <td>{money(preview.totalAfter)}</td>
                              <td>{signed(Math.round(((preview.totalAfter || 0) - (preview.totalBefore || 0)) * 100) / 100)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                    {(preview.trueUp || []).length > 0 && <TrueUpBox rows={preview.trueUp} />}
                    {invoicedChanging.length > 0 && (
                      <label className="depth-confirm-invoiced">
                        <input type="checkbox" checked={confirmInvoiced}
                          onChange={(e) => setConfirmInvoiced(e.target.checked)} />
                        <span>
                          {invoicedChanging.length} stop{invoicedChanging.length === 1 ? ' was' : 's were'} already invoiced
                          ({invoicedChanging.map((s) => s.customer).join(', ')}). Change {invoicedChanging.length === 1 ? 'it' : 'them'} anyway.
                          The sent invoice is not changed; use Bill the difference on the stop afterwards.
                        </span>
                      </label>
                    )}
                  </>
                )}
              </div>

              {sameStormRoutes.length > 0 && depthValue !== null && (
                <label className="depth-same-storm">
                  <input type="checkbox" checked={alsoApply} onChange={(e) => setAlsoApply(e.target.checked)} />
                  <span>Also apply {depthValue.toFixed(1)} in to: {sameStormRoutes.map((r) => r.name).join(', ')}</span>
                </label>
              )}

              {saveError && <div className="depth-alert depth-alert-error">{saveError}</div>}
            </>
          )}
        </div>

        <div className="depth-modal-footer">
          {info && !showHistory && (
            <button className="btn btn-link depth-history-link" onClick={openHistory}>History</button>
          )}
          <span className="depth-spacer" />
          {result ? (
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
              {info && !showHistory && (
                <button className="btn btn-primary" onClick={save} disabled={!canConfirm}>
                  {saving ? 'Saving…' : info.depthInches === null ? 'Save depth' : 'Change depth'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <style>{`
        .depth-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          z-index: 10000; padding: 16px;
        }
        .depth-modal {
          background: var(--color-surface, #fff); border-radius: 12px;
          width: 100%; max-width: 760px; max-height: calc(100vh - 32px);
          display: flex; flex-direction: column; box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }
        .depth-modal-header, .depth-modal-footer {
          display: flex; align-items: center; gap: 8px; padding: 14px 18px;
        }
        .depth-modal-header { border-bottom: 1px solid var(--color-border, #e5e7eb); }
        .depth-modal-footer { border-top: 1px solid var(--color-border, #e5e7eb); }
        .depth-modal-header h2 { margin: 0; font-size: 1.1rem; flex: 1; }
        .depth-modal-close { background: none; border: none; font-size: 1.6rem; cursor: pointer; line-height: 1; color: var(--color-text-light, #6b7280); }
        .depth-modal-body { padding: 16px 18px; overflow-y: auto; }
        .depth-spacer { flex: 1; }
        .depth-history-link { background: none; border: none; color: var(--color-primary, #1e88e5); cursor: pointer; padding: 0; text-decoration: underline; }
        .depth-row { display: flex; gap: 12px; flex-wrap: wrap; }
        .depth-field { display: flex; flex-direction: column; gap: 4px; font-size: 0.85rem; font-weight: 600; width: 160px; }
        .depth-field-wide { flex: 1; min-width: 200px; }
        .depth-field-error { color: #dc2626; font-size: 0.85rem; margin: 6px 0 0; }
        .depth-muted { color: var(--color-text-light, #6b7280); font-size: 0.85rem; font-weight: normal; }
        .depth-section { margin-top: 16px; }
        .depth-section h3 { font-size: 0.95rem; margin: 0 0 8px; }
        .depth-table-wrap { overflow-x: auto; }
        .depth-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .depth-table th, .depth-table td { padding: 6px 8px; border-bottom: 1px solid var(--color-border, #e5e7eb); text-align: left; vertical-align: middle; }
        .depth-table th { font-weight: 600; color: var(--color-text-light, #6b7280); white-space: nowrap; }
        .depth-table tfoot td { font-weight: 600; border-bottom: none; }
        .depth-preview-table tr.changed td { background: rgba(30,136,229,0.05); }
        .depth-preview-table td.up { color: #b45309; font-weight: 600; }
        .depth-preview-table td.down { color: #15803d; font-weight: 600; }
        .depth-city-input { width: 90px; padding: 4px 6px; }
        .depth-city-input.invalid { border-color: #dc2626; }
        .depth-badges { display: inline-flex; gap: 4px; margin-left: 6px; flex-wrap: wrap; }
        .depth-badge { font-size: 0.7rem; padding: 1px 6px; border-radius: 10px; font-weight: 600; }
        .depth-badge.paid { background: #dcfce7; color: #166534; }
        .depth-badge.invoiced { background: #dbeafe; color: #1e40af; }
        .depth-badge.comp { background: #f3e8ff; color: #6b21a8; }
        .depth-alert { padding: 10px 12px; border-radius: 8px; font-size: 0.875rem; margin: 8px 0; }
        .depth-alert-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
        .depth-alert-warning { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
        .depth-alert-success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
        .depth-alert-info { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; }
        .depth-trueup { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 10px 12px; margin-top: 12px; font-size: 0.85rem; }
        .depth-trueup h4 { margin: 0 0 6px; font-size: 0.9rem; color: #92400e; }
        .depth-trueup ul { margin: 0; padding-left: 18px; }
        .depth-confirm-invoiced, .depth-same-storm { display: flex; gap: 8px; align-items: flex-start; margin-top: 12px; font-size: 0.875rem; }
        .depth-confirm-invoiced { background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 10px 12px; }
        .depth-confirm-invoiced input, .depth-same-storm input { margin-top: 3px; }
        .depth-history h3 { font-size: 0.95rem; margin: 12px 0 8px; }
        .depth-history-item { padding: 8px 0; border-bottom: 1px solid var(--color-border, #e5e7eb); }
        .depth-history-head { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
        @media (max-width: 600px) {
          .depth-modal-overlay { padding: 0; align-items: stretch; }
          .depth-modal { max-height: 100vh; border-radius: 0; }
          .depth-field { width: 100%; }
          .depth-table th, .depth-table td { padding: 5px 4px; }
        }
      `}</style>
    </div>
  );
}

// Paid stops whose new price differs from the amount collected at the door.
function TrueUpBox({ rows }) {
  return (
    <div className="depth-trueup">
      <h4>True-up: paid stops whose price changed</h4>
      <ul>
        {rows.map((t) => (
          <li key={t.stopId}>
            {t.customer}: paid {money(t.amountPaid)}, now {money(t.newTotal)}
            {' '}({t.difference > 0 ? `${formatCurrency(t.difference)} still owed` : `${formatCurrency(Math.abs(t.difference))} overpaid`})
          </li>
        ))}
      </ul>
    </div>
  );
}

export default DepthEntryModal;
